import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import supabase from '../supabaseClient';
import {
  getCachedAcademyAccess,
  persistAcademyContext,
  clearAcademyContext,
} from '../utils/academyAccessCache';
import LoaderScreen from './LoaderScreen';
import { recordPlatformAccessEvent } from '../lib/platformAccessEvents';
import { getAdminDemo, endAdminDemo } from '../lib/adminDemoSession';
import { checkSuperAdmin } from '../utils/superAdminUtils';

const AcademyRoute = ({ children }) => {
  const cachedAccess = useMemo(() => getCachedAcademyAccess(), []);
  const [isAcademy, setIsAcademy] = useState(() => (cachedAccess ? true : null));
  const [academyName, setAcademyName] = useState(() => cachedAccess?.academyName || null);

  useEffect(() => {
    const checkAcademy = async () => {
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !sessionData?.session?.user) {
          clearAcademyContext();
          setIsAcademy(false);
          return;
        }

        const userId = sessionData.session.user.id;

        const demo = getAdminDemo('academy', userId);
        if (demo) {
          if (await checkSuperAdmin()) {
            const { data: demoAcademy } = await supabase
              .from('academies')
              .select('name')
              .eq('id', demo.orgId)
              .maybeSingle();
            if (demoAcademy) {
              const demoName = demoAcademy.name || demo.orgName || 'Academy';
              persistAcademyContext({ academyName: demoName, coachName: 'Statbox admin', userId });
              setAcademyName(demoName);
              setIsAcademy(true);
              return;
            }
          }
          endAdminDemo();
        }

        const { data: userProfile, error: profileError } = await supabase
          .from('Users')
          .select('academy_id, name, email, coach, academies(name)')
          .eq('user_id', userId)
          .maybeSingle();

        // Check if user is a coach and has academy access
        const isCoach = userProfile?.coach === true || userProfile?.coach === 'true';
        
        if (profileError || !userProfile || !userProfile.academy_id || !isCoach) {
          clearAcademyContext();
          setIsAcademy(false);
          return;
        }

        const academyDisplayName = userProfile.academies?.name || 'Academy';
        persistAcademyContext({
          academyName: academyDisplayName,
          coachName: userProfile.name,
          userId,
        });
        setIsAcademy(true);
        setAcademyName(academyDisplayName);
        recordPlatformAccessEvent({
          surface: 'academy',
          routePath: window.location?.pathname || '/academy',
          userId,
          waitForSession: false,
          kind: 'visit',
        });
      } catch (error) {
        console.error('Error verifying academy access:', error);
        clearAcademyContext();
        setIsAcademy(false);
      }
    };

    checkAcademy();
  }, []);

  if (isAcademy === null) {
    return (
      <LoaderScreen subtitle={`Verifying ${academyName || 'academy'} access`} />
    );
  }

  return isAcademy ? children : <Navigate to="/academy/login" />;
};

export default AcademyRoute; 